import React, { useEffect, useState } from 'react';
import { Check, Eye, EyeOff, Plus, Save, Trash2 } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import Checkbox from '../common/Checkbox';
import FormField from '../common/FormField';
import Loader from '../common/Loader';
import Modal from '../common/Modal';
import SealCard from '../common/SealCard';

const API = process.env.REACT_APP_API_URL || '';

const emptySeal = () => ({
  kicker: '',
  kicker_en: '',
  title_top_en: '',
  title_bottom_en: '',
  level_en: '',
  caption_en: '',
  kicker_zh: '',
  title_top_zh: '',
  title_bottom_zh: '',
  level_zh: '',
  caption_zh: '',
  title_top: '',
  title_bottom: '',
  level: '',
  caption: '',
  display_order: 0,
  is_active: 1,
});

const HomeSealsTab = () => {
  const { t } = useLanguage();
  const [seals, setSeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [sealToDelete, setSealToDelete] = useState(null);

  useEffect(() => {
    fetchSeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSeals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/home-seals/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('adm_seals_error_load'));
      setSeals(data.map((s) => ({ ...s, _key: `s-${s.id}`, _dirty: false })));
      setError('');
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateLocal = (id, patch, dirty = true) => {
    setSeals((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch, _dirty: dirty } : s)));
  };

  const persist = async (seal) => {
    if (!String(seal.title_top || '').trim()) {
      setError(t('adm_seals_required_title_top'));
      return false;
    }
    setSavingId(seal.id);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/home-seals/${seal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(seal),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('adm_seals_error_save'));
      // El servidor devuelve el sello ya traducido: se pinta al momento, sin recargar
      setSeals((prev) => prev.map((s) => (s.id === seal.id ? { ...seal, ...data, _key: s._key, _dirty: false } : s)));
      setSavedId(seal.id);
      setTimeout(() => setSavedId((current) => (current === seal.id ? null : current)), 2500);
      return true;
    } catch (err) {
      console.error(err);
      setError(err.message);
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const saveSeal = (seal) => persist(seal);

  // La visibilidad se guarda sola: es un interruptor, no hace falta pulsar
  // Guardar para que el sello deje de salir en la landing.
  const toggleVisible = async (seal, visible) => {
    const value = visible ? 1 : 0;
    updateLocal(seal.id, { is_active: value }, false);
    const saved = await persist({ ...seal, is_active: value });
    if (!saved) updateLocal(seal.id, { is_active: seal.is_active }, seal._dirty);
  };

  const confirmDelete = async () => {
    if (!sealToDelete) return;
    const seal = sealToDelete;
    setSealToDelete(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/home-seals/${seal.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || t('adm_seals_error_delete'));
      }
      setSeals((prev) => prev.filter((s) => s.id !== seal.id));
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const createSeal = async () => {
    const draft = emptySeal();
    draft.title_top = t('adm_seals_new_default');
    draft.display_order = seals.length + 1;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/home-seals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('adm_seals_error_create'));
      await fetchSeals();
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  return (
    <div className="tp-seals">
      <header className="tp-admin__block-head tp-seals__head">
        <div>
          <h2 className="tp-admin__block-title">{t('adm_seals_title')}</h2>
          <p className="tp-seals__lead">{t('adm_seals_subtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary btn--sm" onClick={createSeal}>
          <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
          {t('adm_seals_new')}
        </button>
      </header>

      {error && <p className="tp-form__alert" role="alert">{error}</p>}

      {loading ? (
        <div className="tp-admin__loading"><Loader /></div>
      ) : seals.length === 0 ? (
        <p className="tp-admin__empty-text">{t('adm_seals_empty')}</p>
      ) : (
        <div className="tp-seals__list">
          {seals.map((seal) => (
            <article key={seal._key} className={`tp-seals__card ${seal.is_active ? '' : 'is-inactive'}`.trim()}>
              <div className="tp-seals__preview">
                <SealCard seal={seal} />
              </div>

              <div className="tp-form tp-form--bare tp-seals__fields">
                <div className="tp-seals__row">
                  <FormField
                    id={`seal-kicker-${seal.id}`}
                    label={t('adm_seals_kicker')}
                    type="text"
                    value={seal.kicker || ''}
                    maxLength={64}
                    placeholder={t('adm_seals_kicker_placeholder')}
                    onChange={(e) => updateLocal(seal.id, { kicker: e.target.value })}
                  />
                  <FormField
                    id={`seal-order-${seal.id}`}
                    label={t('adm_seals_order')}
                    type="number"
                    value={seal.display_order}
                    onChange={(e) => updateLocal(seal.id, { display_order: Number(e.target.value) || 0 })}
                  />
                </div>

                <div className="tp-seals__row">
                  <FormField
                    id={`seal-top-${seal.id}`}
                    label={t('adm_seals_title_top')}
                    type="text"
                    value={seal.title_top || ''}
                    maxLength={64}
                    placeholder={t('adm_seals_title_top_placeholder')}
                    onChange={(e) => updateLocal(seal.id, { title_top: e.target.value })}
                  />
                  <FormField
                    id={`seal-bottom-${seal.id}`}
                    label={t('adm_seals_title_bottom')}
                    type="text"
                    value={seal.title_bottom || ''}
                    maxLength={64}
                    placeholder={t('adm_seals_title_bottom_placeholder')}
                    onChange={(e) => updateLocal(seal.id, { title_bottom: e.target.value })}
                  />
                </div>

                <div className="tp-seals__row">
                  <FormField
                    id={`seal-level-${seal.id}`}
                    label={t('adm_seals_level')}
                    type="text"
                    value={seal.level || ''}
                    maxLength={48}
                    placeholder={t('adm_seals_level_placeholder')}
                    onChange={(e) => updateLocal(seal.id, { level: e.target.value })}
                  />
                  <div className="tp-seals__visible">
                    <Checkbox
                      checked={Boolean(seal.is_active)}
                      disabled={savingId === seal.id}
                      onChange={(e) => toggleVisible(seal, e.target.checked)}
                    >
                      <span className="tp-seals__visible-label">
                        {seal.is_active
                          ? <Eye size={15} strokeWidth={2} aria-hidden="true" />
                          : <EyeOff size={15} strokeWidth={2} aria-hidden="true" />}
                        {seal.is_active ? t('adm_seals_visible') : t('adm_seals_hidden')}
                      </span>
                    </Checkbox>
                    <p className="tp-seals__visible-hint">{t('adm_seals_visible_hint')}</p>
                  </div>
                </div>

                <FormField
                  id={`seal-caption-${seal.id}`}
                  label={t('adm_seals_caption')}
                  type="text"
                  value={seal.caption || ''}
                  maxLength={255}
                  placeholder={t('adm_seals_caption_placeholder')}
                  onChange={(e) => updateLocal(seal.id, { caption: e.target.value })}
                />

                {[{ code: 'en', title: t('adm_seals_en_title') }, { code: 'zh', title: t('adm_seals_zh_title') }].map((idioma) => (
                  <details className="tp-seals__en" key={idioma.code}>
                    <summary>{idioma.title}</summary>
                    <p className="tp-seals__visible-hint">{t('adm_seals_en_hint')}</p>

                    <div className="tp-seals__row">
                      <FormField
                        id={`seal-kicker-${idioma.code}-${seal.id}`}
                        label={t('adm_seals_kicker')}
                        type="text"
                        value={seal[`kicker_${idioma.code}`] || ''}
                        maxLength={64}
                        onChange={(e) => updateLocal(seal.id, { [`kicker_${idioma.code}`]: e.target.value })}
                      />
                      <FormField
                        id={`seal-level-${idioma.code}-${seal.id}`}
                        label={t('adm_seals_level')}
                        type="text"
                        value={seal[`level_${idioma.code}`] || ''}
                        maxLength={48}
                        onChange={(e) => updateLocal(seal.id, { [`level_${idioma.code}`]: e.target.value })}
                      />
                    </div>

                    <div className="tp-seals__row">
                      <FormField
                        id={`seal-top-${idioma.code}-${seal.id}`}
                        label={t('adm_seals_title_top').replace(' *', '')}
                        type="text"
                        value={seal[`title_top_${idioma.code}`] || ''}
                        maxLength={64}
                        onChange={(e) => updateLocal(seal.id, { [`title_top_${idioma.code}`]: e.target.value })}
                      />
                      <FormField
                        id={`seal-bottom-${idioma.code}-${seal.id}`}
                        label={t('adm_seals_title_bottom')}
                        type="text"
                        value={seal[`title_bottom_${idioma.code}`] || ''}
                        maxLength={64}
                        onChange={(e) => updateLocal(seal.id, { [`title_bottom_${idioma.code}`]: e.target.value })}
                      />
                    </div>

                    <FormField
                      id={`seal-caption-${idioma.code}-${seal.id}`}
                      label={t('adm_seals_caption')}
                      type="text"
                      value={seal[`caption_${idioma.code}`] || ''}
                      maxLength={255}
                      onChange={(e) => updateLocal(seal.id, { [`caption_${idioma.code}`]: e.target.value })}
                    />
                  </details>
                ))}

                <div className="tp-seals__actions">
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => saveSeal(seal)}
                    disabled={!seal._dirty || savingId === seal.id}
                  >
                    <Save size={15} strokeWidth={2} aria-hidden="true" />
                    {savingId === seal.id ? t('adm_seals_saving') : t('adm_seals_save')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--outline btn--sm tp-admin__remove"
                    onClick={() => setSealToDelete(seal)}
                  >
                    <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
                    {t('adm_seals_delete')}
                  </button>

                  {savedId === seal.id && (
                    <span className="tp-seals__saved" role="status">
                      <Check size={15} strokeWidth={2.5} aria-hidden="true" />
                      {t('adm_seals_saved')}
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={Boolean(sealToDelete)}
        onOpenChange={(open) => { if (!open) setSealToDelete(null); }}
        variant="delete"
        title={t('adm_seals_delete')}
        description={sealToDelete ? (t('adm_seals_confirm_delete') || '').replace('{title}', sealToDelete.title_top) : ''}
        cancelText={t('common_cancel')}
        confirmText={t('common_delete')}
        onCancel={() => setSealToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default HomeSealsTab;
